import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, casestudyCards } from ".";
import { CaseStudyHerosection, CaseStudyInformation, ClientFeedback, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const BankingAcademy: React.FC = () => {
    return (
        <NewLayout
            title="Banking Academy | WhatsApp Lead Generation Success Story"
            description="Discover how Banking Academy increased lead generation by 100% and grew business by 49% using Gallabox’s WhatsApp broadcast solutions."
            canonical="https://gallabox.com/case-study/banking-academy"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/banking-academy/banking-academy-og.png" }}
        >
            <CaseStudyHerosection
                title="How Banking Academy scaled lead generation by 100% and grew business by 49% with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/banking-academy/banking-academy-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "49%", label: "overall business growth" },
                    { percentage: "100%", label: "increase in lead generation" },
                    { percentage: "100%", label: "higher customer satisfaction" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Education"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [{ link: "https://gallabox.com/whatsapp-broadcast", label: "WhatsApp Broadcast" }],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About">
                            <p>
                                Based in Gujarat,
                                <a href="https://www.bankingacademy.co.in" className="text-blue-500 px-1">
                                    Banking Academy
                                </a>
                                prepares students for careers in banking and government sectors through specialized coaching. Founded on a commitment to holistic success, the
                                institute combines experienced faculty, unique teaching techniques for aptitude and reasoning, mock interviews with industry professionals, and
                                tech-enabled infrastructure — including CCTV-monitored classrooms and an online test platform.
                            </p>
                            <p className="mt-5">
                                Operating across Riyadh, Jeddah, and Dammam, they&apos;ve become the premium logistics partner for businesses and individuals who refuse to
                                compromise on delivery excellence.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"The challenge: Low visibility and fragmented outreach"} className="mt-0" />

                            <p className="mt-2">
                                Before Gallabox, Banking Academy struggled with two critical gaps: low brand recognition and ineffective customer outreach. As Chetan Bhimani
                                explains, connecting with prospective students was a persistent hurdle. With no centralized system for lead engagement, the team relied on manual
                                follow-ups via SMS and noticeboards—methods that couldn’t scale or personalize interactions.
                            </p>

                            <FeatureList
                                List={[
                                    { listItem: "This fragmented approach limited their ability to:" },
                                    {
                                        listItem: "Nurture potential leads through structured communication.",
                                    },
                                    { listItem: "Retain existing students with timely updates." },
                                    { listItem: "Share time-sensitive vacancy alerts or course details efficiently." },
                                ]}
                                className="mt-4 bg-gray-100 mb-3"
                            />
                            <p>
                                The team tried to bridge these gaps with full internal support and training, but lacked a centralized, scalable platform to drive real-time
                                engagement, nurture leads, and convert interest into enrollments — threatening their expansion goals.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"The solution: Targeted WhatsApp broadcasts"} className="mt-0" />
                            <p className="mt-2">
                                Banking Academy evaluated several tools but found many &quot;unknown or unauthorized.&quot; Gallabox stood out for its marketing capabilities and
                                trusted reputation. &quot;We chose Gallabox because it offered the best way to reach and engage students,&quot; says Bhimani.
                            </p>
                            <p className="text-xl font-medium mt-6">50% boost in employee productivity</p>

                            <FeatureList
                                List={[
                                    { listItem: "Automate lead nurturing: Send personalized course details, fee structures, and enrollment steps to prospective students." },
                                    {
                                        listItem: "Streamline vacancy updates: Instantly share new government/bank job openings with students.",
                                    },
                                    { listItem: "Retain existing learners: Notify current batches about mock tests, syllabus changes, and interview workshops." },
                                ]}
                                className="mt-4 bg-gray-100 mb-3"
                            />
                            <p className="mt-5">
                                Unlike SMS or physical noticeboards, Gallabox’s broadcasts enabled rich-media messaging (PDFs, videos) and real-time tracking—turning WhatsApp into
                                a direct academic advisory channel.
                            </p>
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"Doubled productivity and explosive growth"} className="mt-0" />
                            <p className="mt-2">The transformation was immediate and measurable.</p>

                            <FeatureList
                                List={[
                                    { listItem: "49% overall business growth driven by streamlined enrollment." },
                                    { listItem: "100% increase in lead generation via broadcast campaigns." },
                                    { listItem: "100% higher customer satisfaction through prompt, personalized updates." },

                                    { listItem: "100% surge in employee productivity by replacing manual alerts with automated broadcasts." },
                                    { listItem: "100% growth in conversion rates for first-time students." },
                                    { listItem: "20% rise in gross profits due to optimized resource allocation." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <p className="mt-5">
                                The introduction of Gallabox didn’t just improve operations — it unlocked scale. With automation taking over routine tasks, the team could focus
                                more on teaching and mentoring students.
                            </p>

                            <ClientFeedback
                                descriptions={
                                    <p className="text-gray-900 italic font-semibold px-6 mb-4">
                                        &quot;With Gallabox, our communication is faster, clearer, and far more effective. We’ve seen growth across every critical metric,&quot;
                                    </p>
                                }
                                designationWithServiceName="– Chetan Bhimani, Managing Director, Banking Academy."
                                className="border-blue-500 bg-blue-100  mt-4 "
                            />

                            <CaseStudyInformation subTitle={"Looking ahead"} className="mt-10" />
                            <p className="mt-2">
                                With a solid engagement foundation in place, Banking Academy plans to further integrate Gallabox into its marketing and retention strategies. The
                                team sees opportunities to expand automation to support inquiry handling, follow-up reminders, and long-term student engagement — ensuring that each
                                aspiring candidate receives the timely support and information they need to succeed.
                            </p>

                            <p className="mt-5">
                                For an institute committed to helping students achieve career goals in competitive fields, Gallabox has become more than a tool — it’s a growth
                                partner.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCards.slice(0, 3)?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default BankingAcademy;
